// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
import React, { Component } from 'react';
import * as msTeams from '@microsoft/teams-js';
import { PublicClientApplication, LogLevel } from '@azure/msal-browser';
import { msalConfig, tokenRequest, loginRequest, silentRequest, ISLOCAL } from '../../Constants';
import { getUserDetails } from '../services/graph';
import { checkInTeams } from '../shared/customhooks/useTeams';


const isIE = () => {
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf("MSIE ") > -1;
    const msie11 = ua.indexOf("Trident/") > -1;

    // If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
    // const isEdge = ua.indexOf("Edge/") > -1;

    return msie || msie11;
};

// If you support IE, our recommendation is that you sign-in using Redirect flow
const useRedirectFlow = isIE();

// redirectUri is the app's own origin, so loginPopup/acquireTokenPopup cause this
// entire app (including this HOC) to boot up a second time inside the popup window.
// MSAL's real response handling runs in the OPENER window, which reads the popup's
// URL/localStorage from outside. If this inner popup instance also calls
// handleRedirectPromise(), it races the opener to read/clear the same hash and
// localStorage cache entries - whichever wins determines success vs.
// "BrowserAuthError: hash_empty_error". Skip all MSAL init when we ARE that popup/iframe.
const isMsalPopupOrIframe = () => {
    try {
        return window.opener != null || window.parent !== window.self;
    } catch (e) {
        // Cross-origin access to opener/parent throws - treat as "not the main window".
        return true;
    }
};

const msalApp = new PublicClientApplication(msalConfig);
//const teams = msTeams.initialize();

const withAuthProvider = WrappedComponent => class AuthHOC extends Component {

    constructor(props) {
        super(props);

        this.state = {
            account: null,
            error: null,
            username: null,
            isAuthenticated: false,
            inTeams: checkInTeams(),
            user: null
        };
    }

    componentDidMount = async () => {
    if (isMsalPopupOrIframe()) {
        return;
    }

    try {
        if (typeof msalApp.initialize === "function") {
            await msalApp.initialize();
        }

        const response = await msalApp.handleRedirectPromise();

        if (response) {
            this.handleResponse(response);
        } else {
            this.getAccounts();
        }
    } catch (err) {
        console.error("MSAL initialization error:", err);

        this.setState({
            error: err.errorMessage || err.message
        });

        this.getAccounts();
    }
   };

    getAccounts = () => {
        /**
         * See here for more info on account retrieval:
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
         */
        const currentAccounts = msalApp.getAllAccounts();

        // getAllAccounts() never returns null - it returns [] when there is no
        // cached account, so that case must be checked via length, not === null.
        if (!currentAccounts || currentAccounts.length === 0) {
            console.error("No accounts detected!");
            this.setState({ account: null, username: null, isAuthenticated: false });
            return;
        }

        if (currentAccounts.length > 1) {
            console.warn("Multiple accounts detected.");
            // Add choose account code here
        }

        const activeAccount = currentAccounts[0];
        this.setState({
            username: activeAccount.username,
            account: activeAccount,
            isAuthenticated: true
        });
    }

    getActiveAccount = () => {
        const currentAccounts = msalApp.getAllAccounts();
        if (!currentAccounts || currentAccounts.length === 0) {
            return null;
        }

        if (this.state.username) {
            const match = msalApp.getAccountByUsername(this.state.username);
            if (match) {
                return match;
            }
        }

        return currentAccounts[0];
    }

    handleResponse = (response) => {
        
        if (response !== null) {
            this.setState({
                account: response.account,
                username: response.account.username,
                isAuthenticated: true
            });
        } else {
            this.getAccounts();
        }
        return response;
    }

    acquireTokenMsal = async () => {
        /**
         * See here for more info on account retrieval:
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
         */
        const account = this.getActiveAccount();

        // Spread into new objects instead of mutating the shared silentRequest/tokenRequest
        // singletons imported from Constants - mutating them let concurrent calls overwrite
        // each other's .account and intermittently acquire a token for the wrong user (or none).
        if (account) {
            try {
                return await msalApp.acquireTokenSilent({ ...silentRequest, account });
            } catch (error) {
                console.warn("silent token acquisition fails. acquiring token using interactive method", error);
            }
        } else {
            console.warn("No active account for silent token acquisition; falling back to interactive login");
        }

        // Always fall back to an interactive popup, even with no account yet - this is what
        // actually lets a first-time/no-session user log in and obtain a token. account is
        // omitted (not set to null/undefined explicitly) so MSAL can prompt account selection.
        return msalApp.acquireTokenPopup(account ? { ...tokenRequest, account } : { ...tokenRequest })
            .then(this.handleResponse)
            .catch(err => {
                console.error(err);
                this.setState({ error: err.errorMessage || err.message });
                throw err;
            });
    }

    acquireTokenLocal = async () => {
        return this.acquireTokenMsal();
    }

    acquireTokenProd = async () => {
        return this.acquireTokenMsal();
    }

    setAccountDetailsProd = (token, successCallbackFunction) => {
        this.setState({
            isAuthenticated: true
        });
        successCallbackFunction(token);
        // this.getAccounts();
    }

    acquireTokenProdTeams = async (internalCallbackFn, successCallbackFunction) => {
        msTeams.initialize();
        let authTokenRequest = {
            successCallback: function(token) { 
                internalCallbackFn(token, successCallbackFunction);
            },
            failureCallback: function (error) { console.log("Failure: " + error); },
            resources: [msalConfig.auth.audience]
        }; 
        msTeams.authentication.getAuthToken(authTokenRequest);
    }

    updateUser = (_user) => {
        this.setState({
            user: _user
        });
    }

    getAccessToken = async(successCallbackFunction) => {
        if (ISLOCAL) {
            try {
                const response = await this.acquireTokenLocal();
                if (response && response.accessToken) {
                    successCallbackFunction(response.accessToken);
                }
            } catch (err) {
                console.error("Unable to acquire access token", err);
            }
        }
        else {
            if(this.state.inTeams){
                this.acquireTokenProdTeams(this.setAccountDetailsProd, successCallbackFunction);
            }
            else{
                try {
                    const response = await this.acquireTokenProd();
                    if (response && response.accessToken) {
                        successCallbackFunction(response.accessToken);
                    }
                } catch (err) {
                    console.error("Unable to acquire access token", err);
                }
            }
        }
    }

    signIn = async (redirect) => {
        if (redirect) {
            return msalApp.loginRedirect(loginRequest);
        }

        return msalApp.loginPopup(loginRequest)
            .then(this.handleResponse)
            .catch(err => {
                console.log(err)
                this.setState({ error: err.errorMessage });
            });
    }

    signOut = async (redirect) => {
        const logoutRequest = {
            account: msalApp.getAccountByUsername(this.state.username),
        };

        if(redirect){
            return (await msalApp.logoutRedirect(logoutRequest).then(response => {
                this.setState({
                    account: null,
                    error: null,
                    username: null,
                    isAuthenticated: false,
                    user: null
                });
            })
            .catch(err => {
                console.log(err)
                this.setState({ error: err.errorMessage });
            }));
        }
        else{
            return (await msalApp.logoutPopup(logoutRequest).then(response => {
                this.setState({
                    account: null,
                    error: null,
                    username: null,
                    isAuthenticated: false,
                    user: null
                });
            })
            .catch(err => {
                console.log(err)
                this.setState({ error: err.errorMessage });
            }));
        }

    }

    render() {
        return (
            <WrappedComponent
                {...this.props}
                account={this.state.account}
                error={this.state.error}
                inTeams={this.state.inTeams}
                isAuthenticated={this.state.isAuthenticated}
                user={this.state.user}
                signIn={() => this.signIn(useRedirectFlow)}
                signOut={() => this.signOut(useRedirectFlow)}
                acquireToken={(successCallbackFunction) => this.getAccessToken(successCallbackFunction)}
                updateUser={(_user) => this.updateUser(_user)}
            />
        );
    }
};

export default withAuthProvider;